/**
 * One purchase order: lines, the allocation that produces the landed cost, batches, and the
 * advance and reverse controls.
 *
 * Reaching `available` is the transition that creates inventory, so it is the one with a
 * confirm dialog listing exactly what it does: the batches release, the receipts hit the WAC
 * ledger and the quantity goes to WooCommerce. Reversing says the same thing backwards.
 */
import { useState } from 'react';
import { Card, ConfirmDialog, EmptyState, MoneyText, Pill, Skeleton, useToast } from '@/components';
import { useSession } from '@/app/session';
import { ApiProblem } from '@/lib/api';
import { formatCount } from '@/lib/format';
import { PO_PIPELINE_LABELS, poHasMoney } from './types';
import { useAdvancePo, usePurchaseOrder } from './queries';
import './stock.css';

const FORWARD: Record<string, string> = {
  draft: 'ordered',
  ordered: 'in_transit',
  paid: 'in_transit',
  in_transit: 'received',
  customs: 'received',
  received: 'qc',
  qc: 'available',
  available: 'closed',
};

const BACKWARD: Record<string, string> = {
  ordered: 'draft',
  in_transit: 'ordered',
  customs: 'in_transit',
  received: 'in_transit',
  qc: 'received',
  available: 'qc',
  closed: 'available',
};

export function PoDetailPage({ poId }: { poId: string }) {
  const { can } = useSession();
  const toast = useToast();
  const po = usePurchaseOrder(poId);
  const advance = useAdvancePo();
  const [pending, setPending] = useState<{ to: string; direction: 'forward' | 'back' } | null>(null);

  if (po.isLoading) return <Skeleton shape="card" />;
  const detail = po.data;
  if (!detail) return <EmptyState message="That purchase order does not exist." />;

  const money = poHasMoney(detail) ? detail : null;
  const allocation = detail.allocation ?? null;
  const next = FORWARD[detail.status];
  const back = BACKWARD[detail.status];
  const crossesAvailable = pending?.to === 'available';
  const reversesAvailable = pending?.direction === 'back' && detail.status === 'available';

  const run = async ({ reason }: { reason: string | null }) => {
    if (!pending) return;
    try {
      await advance.mutateAsync({ poId, toStatus: pending.to, reason });
      toast.push({ tone: 'ok', message: `Moved to ${pending.to.replace(/_/g, ' ')}.` });
    } catch (error) {
      toast.push({
        tone: 'crit',
        message: error instanceof Error ? error.message : 'The transition failed.',
        requestId: error instanceof ApiProblem ? error.shortRequestId : null,
      });
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="stock-page">
      <header className="stock-ledger__head">
        <h1 className="stock-ledger__title o-mono">{detail.po_number}</h1>
        <span>{detail.supplier_name}</span>
        <Pill tone={detail.status === 'available' ? 'ok' : 'warn'}>
          {detail.status.replace(/_/g, ' ')}
        </Pill>
        <span className="o-muted">
          {PO_PIPELINE_LABELS[Math.max(detail.pipeline_step - 1, 0)]?.label ?? 'draft'}
        </span>
      </header>

      {/* A packer's detail carries no money and no allocation (CONTRACTS.md 3.6). The
          Money card is not rendered for her, and the lines table is quantities alone. */}
      {money ? (
        <Card title="Money" basis="ledger">
          <dl className="stock-supplier__facts">
            <div>
              <dt>Goods</dt>
              <dd>
                <MoneyText cents={money.goods_cents} />
              </dd>
            </div>
            <div>
              <dt>Freight</dt>
              <dd>
                <MoneyText cents={money.freight_cents} />
              </dd>
            </div>
            <div>
              <dt>Duties</dt>
              <dd>
                <MoneyText cents={money.duties_cents} />
              </dd>
            </div>
            <div>
              <dt>Other</dt>
              <dd>
                <MoneyText cents={money.other_cents} />
              </dd>
            </div>
            <div>
              <dt>Total landed</dt>
              <dd>
                <MoneyText cents={money.total_landed_cents} />
              </dd>
            </div>
            <div>
              <dt>Paid to date</dt>
              <dd>
                <MoneyText cents={money.paid_to_date_cents} />
              </dd>
            </div>
            <div>
              <dt>Balance due</dt>
              <dd>
                <MoneyText cents={money.balance_due_cents ?? null} />
              </dd>
            </div>
            <div>
              <dt>Freight allocated by</dt>
              <dd>{money.freight_allocation ?? 'units'}</dd>
            </div>
          </dl>
        </Card>
      ) : null}

      <Card title={allocation ? 'Lines and allocation' : 'Lines'} basis="ledger">
        <div className="stock-scroll" data-allow-hscroll>
          <table className="stock-table">
            <caption className="o-visually-hidden">
              {allocation ? 'Purchase order lines with allocated costs' : 'Purchase order lines'}
            </caption>
            <thead>
              <tr>
                <th scope="col">SKU</th>
                <th scope="col" className="stock-num">Ordered</th>
                <th scope="col" className="stock-num">Received</th>
                <th scope="col" className="stock-num">Rejected</th>
                {allocation ? (
                  <>
                    <th scope="col" className="stock-num">Unit cost</th>
                    <th scope="col" className="stock-num">Goods</th>
                    <th scope="col" className="stock-num">Allocated</th>
                    <th scope="col" className="stock-num">Landed unit</th>
                  </>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {detail.lines.map((line) => {
                const alloc = allocation?.lines.find((a) => a.po_line_id === line.po_line_id);
                return (
                  <tr key={line.po_line_id}>
                    <td className="o-mono">{line.sku ?? line.name ?? line.product_id}</td>
                    <td className="stock-num">{formatCount(line.qty_ordered)}</td>
                    <td className="stock-num">{formatCount(line.qty_received)}</td>
                    <td className="stock-num">{formatCount(line.qty_rejected)}</td>
                    {allocation ? (
                      <>
                        <td className="stock-num">
                          <MoneyText cents={line.unit_cost_cents ?? null} showCents />
                        </td>
                        <td className="stock-num">
                          <MoneyText cents={alloc?.goods_cents ?? line.line_total_cents ?? null} />
                        </td>
                        <td className="stock-num">
                          <MoneyText cents={alloc?.allocated_cents ?? 0} />
                        </td>
                        <td className="stock-num">
                          <MoneyText
                            cents={alloc?.landed_unit_cents ?? line.unit_cost_cents ?? null}
                            showCents
                          />
                        </td>
                      </>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
            {allocation ? (
              <tfoot>
                <tr>
                  <th scope="row" colSpan={5}>
                    Totals
                  </th>
                  <td className="stock-num">
                    <MoneyText cents={allocation.goods_total_cents} />
                  </td>
                  <td className="stock-num">
                    <MoneyText cents={allocation.allocated_total_cents} />
                  </td>
                  <td className="stock-num">
                    <MoneyText cents={allocation.total_landed_cents} />
                  </td>
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      </Card>

      <Card title="Batches" basis="ledger">
        {detail.batches.length === 0 ? (
          <EmptyState message="Nothing received yet. Batches appear when the receiving flow runs." />
        ) : (
          <table className="stock-table">
            <caption className="o-visually-hidden">Batches created by this purchase order</caption>
            <thead>
              <tr>
                <th scope="col">Lot</th>
                <th scope="col" className="stock-num">Qty</th>
                <th scope="col">Status</th>
                <th scope="col">Received</th>
                <th scope="col">Available</th>
                {money ? <th scope="col" className="stock-num">Landed unit</th> : null}
                <th scope="col">COA</th>
              </tr>
            </thead>
            <tbody>
              {detail.batches.map((batch) => (
                <tr key={batch.batch_id}>
                  <td className="o-mono">{batch.lot_number ?? batch.batch_id.slice(-6)}</td>
                  <td className="stock-num">{formatCount(batch.qty_received)}</td>
                  <td>
                    <Pill tone={batch.status === 'available' ? 'ok' : 'warn'}>{batch.status}</Pill>
                  </td>
                  <td>{batch.received_date ?? 'not yet'}</td>
                  <td>{batch.available_date ?? 'not yet'}</td>
                  {money ? (
                    <td className="stock-num">
                      <MoneyText cents={batch.landed_unit_cents ?? null} showCents />
                    </td>
                  ) : null}
                  <td>{batch.coa_present ? <Pill tone="ok">yes</Pill> : <Pill tone="warn">missing</Pill>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {can('po.manage') ? (
        <div className="stock-po__actions">
          {next ? (
            <button
              type="button"
              className="o-btn"
              onClick={() => setPending({ to: next, direction: 'forward' })}
            >
              Advance to {next.replace(/_/g, ' ')}
            </button>
          ) : null}
          {back ? (
            <button
              type="button"
              className="o-btn o-btn--ghost"
              onClick={() => setPending({ to: back, direction: 'back' })}
            >
              Reverse to {back.replace(/_/g, ' ')}
            </button>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={pending !== null}
        title={pending?.direction === 'back' ? 'Reverse this purchase order' : 'Advance this purchase order'}
        body={`${detail.po_number} moves from ${detail.status.replace(/_/g, ' ')} to ${(pending?.to ?? '').replace(/_/g, ' ')}.`}
        effects={
          crossesAvailable
            ? [
                'Releases every batch on this purchase order.',
                'Writes a receipt into the WAC ledger for each one, which moves the weighted average cost.',
                'Pushes the quantity into WooCommerce through the bridge, compare and set.',
              ]
            : reversesAvailable
              ? [
                  'Quarantines every batch on this purchase order.',
                  'Writes a reversing adjustment into the WAC ledger for each one.',
                  'Pulls the quantity back out of WooCommerce.',
                ]
              : ['Stamps the transition date. Stock and cost are untouched.']
        }
        confirmLabel={pending?.direction === 'back' ? 'Reverse' : 'Advance'}
        reasonRequired={crossesAvailable || reversesAvailable}
        destructive={reversesAvailable}
        note="Owner action. Audited with the before and after status."
        onConfirm={run}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}
